import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, ArrowLeft, Ban, CalendarDays, CheckCircle2, Download, FileText,
    Loader2, Plus, Receipt, RefreshCw, Send, X,
} from 'lucide-react';
import { Button } from '../Button';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { InvoiceConfig, QuoteConfig } from '../../types';
import { Freelancer, formatCents, formatSteuersatz, heuteIso } from '../../services/freelancerLogic';
import { formatZeitraum } from '../../services/assignmentLogic';
import {
    CREDIT_NOTE_STATUS_KLASSEN,
    CREDIT_NOTE_STATUS_LABELS,
    FreelancerCreditNote,
    FreelancerCreditNoteLine,
    KLEINUNTERNEHMER_HINWEIS,
    faelligAm,
    istKleinunternehmer,
    istUeberfaellig,
    kannStornieren,
    sortiereCreditNotes,
    summeBruttoCents,
} from '../../services/freelancerCreditNoteLogic';
import {
    LaufVorschauErgebnis,
    cancelCreditNote,
    createCreditNoteRun,
    deleteCreditNoteDraft,
    fetchCreditNoteLines,
    fetchCreditNotes,
    getCreditNotePdfUrl,
    issueCreditNote,
    ladeLaufVorschau,
    markCreditNotePaid,
    markCreditNoteSent,
    speichereCreditNotePdf,
} from '../../services/freelancerCreditNoteService';
import { renderCreditNotePdfBytes } from '../../services/pdf/generatorCreditNote';
import { buildInvoiceCompanyForPdf } from '../../services/pdf/companyForPdf';
import { exportCreditNotesDatev } from '../../services/datev/creditNoteDatev';

// Gutschriften an Freelancer (ERP/CRM-Plan Abschnitt 7 „Workflow" Schritt 5 —
// Phase D5).
//
// Duenner Container (Standing Decision): laedt Belege, haelt die Auswahl und
// ruft die RPCs auf. Jede fachliche Aussage — welche Knoepfe erscheinen, wann
// ein Beleg ueberfaellig ist, was der Monatslauf buendeln wuerde, wie die
// Betraege lauten — kommt fertig aus freelancerCreditNoteLogic.ts.
//
// Der Beleg ist ab dem Ausstellen UNVERAENDERLICH (Trigger in
// supabase/freelancer_credit_notes.sql). Diese Ansicht kennt deshalb kein
// Bearbeiten: nur Anlegen, Ausstellen, PDF, Versandvermerk, Storno und
// Zahlungseingang.

interface Props {
    freelancers: Freelancer[];
    /** Firmenstammdaten fuer den PDF-Briefkopf (App.tsx, invoiceCompany). */
    company?: { name?: string; address?: string; logo?: string; footerText?: string; senderLine?: string };
    invoiceConfig?: InvoiceConfig;
    quoteConfig?: unknown;
    /** Name unter der Grussformel des PDF. */
    userName?: string;
}

const StatusAbzeichen: React.FC<{ beleg: FreelancerCreditNote; heute: string }> = ({ beleg, heute }) => (
    <span className="inline-flex items-center gap-1.5">
        <span
            className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold border ${CREDIT_NOTE_STATUS_KLASSEN[beleg.status]}`}
        >
            {CREDIT_NOTE_STATUS_LABELS[beleg.status]}
        </span>
        {istUeberfaellig(beleg, heute) && (
            <span className="inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-bold border bg-amber-50 text-amber-700 border-amber-200">
                überfällig
            </span>
        )}
    </span>
);

export const CreditNotesView: React.FC<Props> = ({
    freelancers,
    company,
    invoiceConfig,
    quoteConfig,
    userName,
}) => {
    const heute = heuteIso();

    const [belege, setBelege] = useState<FreelancerCreditNote[]>([]);
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [hinweis, setHinweis] = useState<string | null>(null);
    const [arbeitetAn, setArbeitetAn] = useState<string | null>(null);

    const [detailId, setDetailId] = useState<string | null>(null);
    const [lines, setLines] = useState<FreelancerCreditNoteLine[]>([]);
    const [linesLaedt, setLinesLaedt] = useState(false);

    // ── Monatslauf ──
    const [laufOffen, setLaufOffen] = useState(false);
    const [laufFreelancer, setLaufFreelancer] = useState('');
    const [laufStichtag, setLaufStichtag] = useState(heute);
    const [vorschau, setVorschau] = useState<LaufVorschauErgebnis | null>(null);
    const [vorschauLaedt, setVorschauLaedt] = useState(false);

    // ── Storno ──
    const [stornoFuer, setStornoFuer] = useState<FreelancerCreditNote | null>(null);
    const [stornoGrund, setStornoGrund] = useState('');

    // ── DATEV ──
    const [datevVon, setDatevVon] = useState(`${heute.slice(0, 7)}-01`);
    const [datevBis, setDatevBis] = useState(heute);

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            setBelege(await fetchCreditNotes());
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLaedt(false);
        }
    }, []);

    useEffect(() => {
        void laden();
    }, [laden]);

    const freelancerNamen = useMemo(() => {
        const m = new Map<string, Freelancer>();
        for (const f of freelancers) m.set(f.id, f);
        return m;
    }, [freelancers]);

    const sortiert = useMemo(() => sortiereCreditNotes(belege), [belege]);
    const nummernNachId = useMemo(() => {
        const m = new Map<string, string>();
        for (const b of belege) if (b.nummer) m.set(b.id, b.nummer);
        return m;
    }, [belege]);

    const detail = useMemo(
        () => belege.find((b) => b.id === detailId) ?? null,
        [belege, detailId],
    );

    // Positionen nur fuer den geoeffneten Beleg — eine Abfrage je Zeile der
    // Liste waere ein N+1 auf einer Seite, die die Positionen gar nicht zeigt.
    useEffect(() => {
        if (!detailId) {
            setLines([]);
            return;
        }
        let abgebrochen = false;
        setLinesLaedt(true);
        fetchCreditNoteLines(detailId)
            .then((l) => { if (!abgebrochen) setLines(l); })
            .catch((e) => { if (!abgebrochen) setFehler(rechtFehlerOder(e)); })
            .finally(() => { if (!abgebrochen) setLinesLaedt(false); });
        return () => { abgebrochen = true; };
    }, [detailId]);

    // ── Vorschau des Laufs ────────────────────────────────────────────────
    const vorschauLaden = useCallback(async (freelancerId: string, bis: string) => {
        if (!freelancerId || !bis) {
            setVorschau(null);
            return;
        }
        setVorschauLaedt(true);
        setFehler(null);
        try {
            const f = freelancerNamen.get(freelancerId);
            setVorschau(await ladeLaufVorschau(freelancerId, bis, f?.steuersatzBp ?? 1900));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
            setVorschau(null);
        } finally {
            setVorschauLaedt(false);
        }
    }, [freelancerNamen]);

    useEffect(() => {
        if (laufOffen) void vorschauLaden(laufFreelancer, laufStichtag);
    }, [laufOffen, laufFreelancer, laufStichtag, vorschauLaden]);

    // ── Aktionen ──────────────────────────────────────────────────────────
    const handeln = async (id: string, fn: () => Promise<string | void>) => {
        setArbeitetAn(id);
        setFehler(null);
        setHinweis(null);
        try {
            const meldung = await fn();
            // Neu laden statt lokal umzubiegen: die Datenbank entscheidet
            // verbindlich, und ein Storno erzeugt einen Beleg, den dieser
            // Bildschirm noch nicht kennt.
            await laden();
            if (typeof meldung === 'string' && meldung) setHinweis(meldung);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setArbeitetAn(null);
        }
    };

    const laufStarten = async () => {
        if (!laufFreelancer) return;
        setArbeitetAn('lauf');
        setFehler(null);
        try {
            const neu = await createCreditNoteRun(laufFreelancer, laufStichtag);
            setLaufOffen(false);
            await laden();
            setDetailId(neu.id);
            setHinweis('Der Entwurf wurde angelegt. Bitte prüfen und anschließend ausstellen.');
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setArbeitetAn(null);
        }
    };

    /**
     * PDF erzeugen und ablegen.
     *
     * Der Briefkopf kommt aus derselben Quelle wie beim Rechnungs-PDF
     * (buildInvoiceCompanyForPdf) — sonst traegt die Gutschrift einen anderen
     * Briefkopf als die Rechnung desselben Hauses.
     */
    const pdfErzeugen = async (beleg: FreelancerCreditNote) => {
        const companyForPdf = buildInvoiceCompanyForPdf(
            company,
            quoteConfig as QuoteConfig | undefined,
            invoiceConfig,
        );
        const belegLines = beleg.id === detailId ? lines : await fetchCreditNoteLines(beleg.id);
        // Beim Storno stehen die Positionen des URSPRUNGSbelegs auf dem Papier
        // — der Storno selbst traegt keine (Begruendung: freelancer_credit_
        // notes.sql, Abschnitt 5c).
        const gedruckteLines = beleg.stornoOf
            ? await fetchCreditNoteLines(beleg.stornoOf)
            : belegLines;
        const bytes = await renderCreditNotePdfBytes({
            beleg,
            lines: gedruckteLines,
            company: companyForPdf,
            config: invoiceConfig,
            originalNummer: beleg.stornoOf ? nummernNachId.get(beleg.stornoOf) : undefined,
            userName,
        });
        await speichereCreditNotePdf(beleg, bytes);
        return 'Das PDF wurde erzeugt und abgelegt.';
    };

    const pdfOeffnen = async (beleg: FreelancerCreditNote) => {
        if (!beleg.pdfPfad) return;
        const url = await getCreditNotePdfUrl(beleg.pdfPfad);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const datevExport = () => {
        setFehler(null);
        try {
            const auswahl = belege.filter(
                (b) =>
                    b.status !== 'entwurf'
                    && !!b.ausgestelltAm
                    && b.ausgestelltAm >= datevVon
                    && b.ausgestelltAm <= datevBis,
            );
            if (auswahl.length === 0) {
                setFehler('In diesem Zeitraum gibt es keine ausgestellten Gutschriften.');
                return;
            }
            const { fileName, bytes } = exportCreditNotesDatev(
                auswahl,
                invoiceConfig?.datev,
                { from: datevVon, to: datevBis },
                { exportedBy: userName },
            );
            const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            // Nicht sofort widerrufen: manche Browser brechen den Download ab,
            // wenn die URL im selben Tick ungueltig wird.
            setTimeout(() => URL.revokeObjectURL(url), 10_000);
            setHinweis(`${auswahl.length} Belege exportiert (${fileName}).`);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        }
    };

    // ── Detailansicht ─────────────────────────────────────────────────────
    if (detail) {
        const f = freelancerNamen.get(detail.freelancerId);
        const faellig = faelligAm(detail.ausgestelltAm, detail.zahlungszielTage);
        const busy = arbeitetAn === detail.id;
        return (
            <div className="space-y-4">
                <button
                    onClick={() => setDetailId(null)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Zurück zur Übersicht
                </button>

                {fehler && <Fehlerband text={fehler} onClose={() => setFehler(null)} />}
                {hinweis && <Hinweisband text={hinweis} onClose={() => setHinweis(null)} />}

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800">
                                {detail.nummer || 'Entwurf'}
                                {detail.stornoOf && (
                                    <span className="ml-2 text-sm font-semibold text-rose-600">
                                        Storno zu {nummernNachId.get(detail.stornoOf) || '—'}
                                    </span>
                                )}
                            </h3>
                            <p className="text-xs text-slate-500">
                                {f?.name || 'Unbekannter Freelancer'}
                                {f?.firma ? ` · ${f.firma}` : ''}
                            </p>
                        </div>
                        <StatusAbzeichen beleg={detail} heute={heute} />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <Feld label="Leistungszeitraum" wert={formatZeitraum(detail.leistungszeitraumVon, detail.leistungszeitraumBis)} />
                        <Feld label="Netto" wert={formatCents(detail.nettoCents)} />
                        <Feld
                            label={istKleinunternehmer(detail.steuersatzBp) ? 'Umsatzsteuer' : `USt (${formatSteuersatz(detail.steuersatzBp)})`}
                            wert={istKleinunternehmer(detail.steuersatzBp) ? '—' : formatCents(detail.ustCents)}
                        />
                        <Feld label="Brutto" wert={formatCents(detail.bruttoCents)} />
                        <Feld label="Ausgestellt am" wert={detail.ausgestelltAm ? formatZeitraum(detail.ausgestelltAm, detail.ausgestelltAm) : '—'} />
                        <Feld label="Zahlung bis" wert={faellig ? formatZeitraum(faellig, faellig) : '—'} />
                        <Feld label="Bezahlt am" wert={detail.paidAt ? formatZeitraum(detail.paidAt, detail.paidAt) : '—'} />
                        <Feld label="PDF" wert={detail.pdfPfad ? 'abgelegt' : 'noch nicht erzeugt'} />
                    </div>

                    {istKleinunternehmer(detail.steuersatzBp) && (
                        <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
                            {KLEINUNTERNEHMER_HINWEIS} Dieser Hinweis steht auch auf dem Beleg.
                        </p>
                    )}
                    {detail.stornoGrund && (
                        <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-4 py-2.5">
                            Stornogrund: {detail.stornoGrund}
                        </p>
                    )}

                    {/* ── Aktionen ── */}
                    <div className="flex flex-wrap gap-2 pt-1">
                        {detail.status === 'entwurf' && (
                            <Button
                                variant="primary"
                                disabled={busy}
                                onClick={() => handeln(detail.id, async () => {
                                    const nummer = await issueCreditNote(detail.id);
                                    return `Gutschrift ${nummer} ausgestellt.`;
                                })}
                                icon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            >
                                Ausstellen
                            </Button>
                        )}
                        {detail.status === 'entwurf' && (
                            <Button
                                variant="danger"
                                disabled={busy}
                                onClick={() => handeln(detail.id, async () => {
                                    await deleteCreditNoteDraft(detail.id);
                                    setDetailId(null);
                                    return 'Der Entwurf wurde verworfen.';
                                })}
                                icon={<Ban className="w-4 h-4" />}
                            >
                                Entwurf verwerfen
                            </Button>
                        )}
                        {detail.status !== 'entwurf' && (
                            <Button
                                variant="secondary"
                                disabled={busy}
                                onClick={() => handeln(detail.id, () => pdfErzeugen(detail))}
                                icon={<FileText className="w-4 h-4" />}
                            >
                                PDF erzeugen
                            </Button>
                        )}
                        {detail.pdfPfad && (
                            <Button
                                variant="secondary"
                                onClick={() => { void pdfOeffnen(detail).catch((e) => setFehler(rechtFehlerOder(e))); }}
                                icon={<Download className="w-4 h-4" />}
                            >
                                PDF herunterladen
                            </Button>
                        )}
                        {detail.status === 'ausgestellt' && (
                            <Button
                                variant="secondary"
                                disabled={busy}
                                onClick={() => handeln(detail.id, async () => {
                                    await markCreditNoteSent(detail.id);
                                    return 'Als versendet vermerkt.';
                                })}
                                icon={<Send className="w-4 h-4" />}
                            >
                                Als versendet vermerken
                            </Button>
                        )}
                        {(detail.status === 'ausgestellt' || detail.status === 'versendet') && (
                            <Button
                                variant="secondary"
                                disabled={busy}
                                onClick={() => handeln(detail.id, async () => {
                                    await markCreditNotePaid(detail.id, heute);
                                    return 'Zahlungsausgang gebucht.';
                                })}
                                icon={<CheckCircle2 className="w-4 h-4" />}
                            >
                                Als bezahlt markieren
                            </Button>
                        )}
                        {kannStornieren(detail) && (
                            <Button
                                variant="secondary"
                                onClick={() => { setStornoGrund(''); setStornoFuer(detail); }}
                                icon={<Ban className="w-4 h-4" />}
                            >
                                Stornieren
                            </Button>
                        )}
                    </div>

                    {detail.status !== 'entwurf' && !detail.stornoOf && (
                        <p className="text-[11px] text-slate-400">
                            Dieser Beleg ist festgeschrieben. Eine Korrektur ist ausschließlich
                            über einen Storno-Beleg möglich.
                        </p>
                    )}
                </div>

                {/* ── Positionen ── */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <h4 className="px-6 py-4 text-sm font-bold text-slate-700 border-b border-slate-100">
                        Abgerechnete Begehungen
                    </h4>
                    {linesLaedt ? (
                        <div className="py-10 text-center text-slate-400">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                        </div>
                    ) : lines.length === 0 ? (
                        <p className="px-6 py-8 text-xs text-slate-400 italic">
                            {detail.stornoOf
                                ? 'Ein Storno-Beleg trägt keine eigenen Positionen — er hebt den Ursprungsbeleg als Ganzes auf.'
                                : 'Keine Positionen.'}
                        </p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 bg-slate-50/70 border-b border-slate-100">
                                    <th className="px-6 py-3 font-bold">Projekt / Begehung</th>
                                    <th className="px-6 py-3 font-bold">Leistungsdatum</th>
                                    <th className="px-6 py-3 font-bold text-right">Betrag</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((l) => (
                                    <tr key={l.id} className="border-b border-slate-50 last:border-0">
                                        <td className="px-6 py-3 text-slate-700">
                                            {l.descriptionSnapshot}
                                            {l.voidedAt && (
                                                <span className="ml-2 text-[11px] text-rose-600 font-bold">
                                                    storniert
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-3 text-slate-600">
                                            {formatZeitraum(l.serviceDate, l.serviceDate)}
                                        </td>
                                        <td className="px-6 py-3 text-right tabular-nums font-semibold text-slate-800">
                                            {formatCents(l.netCents)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {stornoFuer && (
                    <StornoDialog
                        beleg={stornoFuer}
                        grund={stornoGrund}
                        setGrund={setStornoGrund}
                        laeuft={arbeitetAn === stornoFuer.id}
                        onClose={() => setStornoFuer(null)}
                        onBestaetigen={() => {
                            const ziel = stornoFuer;
                            setStornoFuer(null);
                            void handeln(ziel.id, async () => {
                                const storno = await cancelCreditNote(ziel.id, stornoGrund);
                                setDetailId(storno?.id ?? null);
                                return `Storno-Beleg ${storno?.nummer || ''} erstellt. Die Begehungen sind wieder abrechenbar.`;
                            });
                        }}
                    />
                )}
            </div>
        );
    }

    // ── Übersicht ─────────────────────────────────────────────────────────
    return (
        <div className="space-y-4">
            {fehler && <Fehlerband text={fehler} onClose={() => setFehler(null)} />}
            {hinweis && <Hinweisband text={hinweis} onClose={() => setHinweis(null)} />}

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col lg:flex-row gap-4 lg:items-end justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-teal-50 text-teal-600 rounded-xl">
                        <Receipt className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-slate-800">Gutschriften</h3>
                        <p className="text-[11px] text-slate-500">
                            {belege.length} Belege · Summe {formatCents(summeBruttoCents(belege))}
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 items-end">
                    {/* DATEV-Zeitraum. Bezugspunkt ist das AUSSTELLUNGSdatum —
                        der Steuerberater bucht nach Belegdatum. */}
                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                        Von
                        <input
                            type="date"
                            value={datevVon}
                            onChange={(e) => setDatevVon(e.target.value)}
                            className="block mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-normal normal-case tracking-normal text-slate-700"
                        />
                    </label>
                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                        Bis
                        <input
                            type="date"
                            value={datevBis}
                            onChange={(e) => setDatevBis(e.target.value)}
                            className="block mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-normal normal-case tracking-normal text-slate-700"
                        />
                    </label>
                    <Button variant="secondary" onClick={datevExport} icon={<Download className="w-4 h-4" />}>
                        DATEV-Export
                    </Button>
                    <Button variant="secondary" onClick={laden} icon={<RefreshCw className={`w-4 h-4 ${laedt ? 'animate-spin' : ''}`} />}>
                        Neu laden
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => { setLaufFreelancer(''); setVorschau(null); setLaufOffen(true); }}
                        icon={<Plus className="w-4 h-4" />}
                    >
                        Monatslauf
                    </Button>
                </div>
            </div>

            {laedt ? (
                <div className="py-20 text-center text-slate-400 bg-white rounded-2xl border border-slate-200">
                    <Loader2 className="w-7 h-7 animate-spin mx-auto mb-3 text-blue-500" />
                    <p className="text-sm font-medium">Gutschriften werden geladen…</p>
                </div>
            ) : sortiert.length === 0 ? (
                <div className="py-16 text-center text-slate-400 bg-white rounded-2xl border-2 border-dashed border-slate-100">
                    <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                    <p className="font-bold text-slate-600">Noch keine Gutschrift</p>
                    <p className="text-xs mt-1">
                        Über „Monatslauf" werden alle geprüften Begehungen eines Freelancers
                        zu einem Beleg gebündelt.
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 bg-slate-50/70 border-b border-slate-100">
                                    <th className="px-4 py-3 font-bold">Nummer</th>
                                    <th className="px-4 py-3 font-bold">Freelancer</th>
                                    <th className="px-4 py-3 font-bold">Zeitraum</th>
                                    <th className="px-4 py-3 font-bold text-right">Betrag</th>
                                    <th className="px-4 py-3 font-bold">Fällig / bezahlt</th>
                                    <th className="px-4 py-3 font-bold">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortiert.map((b) => {
                                    const f = freelancerNamen.get(b.freelancerId);
                                    const faellig = faelligAm(b.ausgestelltAm, b.zahlungszielTage);
                                    return (
                                        <tr
                                            key={b.id}
                                            onClick={() => setDetailId(b.id)}
                                            className="border-b border-slate-50 last:border-0 hover:bg-blue-50/40 cursor-pointer"
                                        >
                                            <td className="px-4 py-3 font-mono text-slate-700">
                                                {b.nummer || <span className="italic text-slate-400">Entwurf</span>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="font-semibold text-slate-800">{f?.name || '—'}</span>
                                                {f?.firma && (
                                                    <span className="block text-[11px] text-slate-400">{f.firma}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">
                                                {formatZeitraum(b.leistungszeitraumVon, b.leistungszeitraumBis) || '—'}
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-800">
                                                {formatCents(b.bruttoCents)}
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">
                                                {b.paidAt
                                                    ? `bezahlt ${formatZeitraum(b.paidAt, b.paidAt)}`
                                                    : faellig
                                                      ? formatZeitraum(faellig, faellig)
                                                      : '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusAbzeichen beleg={b} heute={heute} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Monatslauf-Dialog ── */}
            {laufOffen && (
                <div className="fixed inset-0 z-[100] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-slate-800">Monatslauf</h3>
                            <button onClick={() => setLaufOffen(false)} className="p-1.5 text-slate-400 hover:text-slate-600">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="space-y-1.5 block">
                                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                                    Freelancer
                                </span>
                                <select
                                    value={laufFreelancer}
                                    onChange={(e) => setLaufFreelancer(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                                >
                                    <option value="">— bitte wählen —</option>
                                    {freelancers.map((f) => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}{f.firma ? ` (${f.firma})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="space-y-1.5 block">
                                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                                    Stichtag
                                </span>
                                <input
                                    type="date"
                                    value={laufStichtag}
                                    onChange={(e) => setLaufStichtag(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                                />
                            </label>
                        </div>

                        <p className="text-[11px] text-slate-400 ml-1">
                            Gebündelt werden alle geprüften Begehungen, deren Zeitraum bis zum
                            Stichtag endet und die noch auf keiner gültigen Gutschrift stehen.
                        </p>

                        {/* ── Vorschau ── */}
                        <div className="border border-slate-200 rounded-xl overflow-hidden">
                            {vorschauLaedt ? (
                                <div className="py-8 text-center text-slate-400">
                                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                                </div>
                            ) : !vorschau || vorschau.assignments.length === 0 ? (
                                <p className="px-4 py-6 text-xs text-slate-400 italic text-center">
                                    {laufFreelancer
                                        ? 'Für diesen Freelancer gibt es bis zum Stichtag nichts abzurechnen.'
                                        : 'Bitte einen Freelancer wählen.'}
                                </p>
                            ) : (
                                <>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 bg-slate-50/70 border-b border-slate-100">
                                                <th className="px-4 py-2.5 font-bold">Projekt</th>
                                                <th className="px-4 py-2.5 font-bold">Zeitraum</th>
                                                <th className="px-4 py-2.5 font-bold text-right">Vergütung</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {vorschau.assignments.map((a) => (
                                                <tr key={a.id} className="border-b border-slate-50 last:border-0">
                                                    <td className="px-4 py-2.5 text-slate-700">
                                                        {vorschau.projektNamen.get(a.projectId) || 'Begehung'}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-slate-600">
                                                        {formatZeitraum(a.zeitraumVon, a.zeitraumBis)}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                                                        {formatCents(a.verguetungCents)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="bg-slate-50 px-4 py-3 text-sm space-y-1">
                                        <SummeZeile label="Netto" wert={formatCents(vorschau.betraege.nettoCents)} />
                                        {!istKleinunternehmer(freelancerNamen.get(laufFreelancer)?.steuersatzBp ?? 1900) && (
                                            <SummeZeile
                                                label={`USt (${formatSteuersatz(freelancerNamen.get(laufFreelancer)?.steuersatzBp ?? 1900)})`}
                                                wert={formatCents(vorschau.betraege.ustCents)}
                                            />
                                        )}
                                        <SummeZeile label="Brutto" wert={formatCents(vorschau.betraege.bruttoCents)} fett />
                                        {istKleinunternehmer(freelancerNamen.get(laufFreelancer)?.steuersatzBp ?? 1900) && (
                                            <p className="text-[11px] text-slate-500 pt-1">{KLEINUNTERNEHMER_HINWEIS}</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-1">
                            <Button variant="secondary" onClick={() => setLaufOffen(false)}>Abbrechen</Button>
                            <Button
                                variant="primary"
                                onClick={laufStarten}
                                disabled={
                                    arbeitetAn === 'lauf'
                                    || !laufFreelancer
                                    || !vorschau
                                    || vorschau.assignments.length === 0
                                }
                                icon={
                                    arbeitetAn === 'lauf'
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : <CalendarDays className="w-4 h-4" />
                                }
                            >
                                Entwurf anlegen
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Kleinteile ─────────────────────────────────────────────────────────────

const Feld: React.FC<{ label: string; wert?: string | null }> = ({ label, wert }) => (
    <div className="space-y-1">
        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{label}</p>
        <p className="text-sm text-slate-700 tabular-nums">{wert || '—'}</p>
    </div>
);

const SummeZeile: React.FC<{ label: string; wert: string; fett?: boolean }> = ({ label, wert, fett }) => (
    <div className={`flex justify-between ${fett ? 'font-bold text-slate-800 pt-1 border-t border-slate-200' : 'text-slate-600'}`}>
        <span>{label}</span>
        <span className="tabular-nums">{wert}</span>
    </div>
);

const Fehlerband: React.FC<{ text: string; onClose: () => void }> = ({ text, onClose }) => (
    <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl p-4 text-sm flex items-start justify-between gap-3">
        <span className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            {text}
        </span>
        <button onClick={onClose} className="shrink-0 hover:bg-rose-100 rounded p-1">
            <X className="w-4 h-4" />
        </button>
    </div>
);

const Hinweisband: React.FC<{ text: string; onClose: () => void }> = ({ text, onClose }) => (
    <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl p-4 text-sm flex items-start justify-between gap-3">
        <span className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            {text}
        </span>
        <button onClick={onClose} className="shrink-0 hover:bg-emerald-100 rounded p-1">
            <X className="w-4 h-4" />
        </button>
    </div>
);

/**
 * Storno-Dialog mit PFLICHT-Grund.
 *
 * Der Grund ist keine Hoeflichkeit: die RPC weist einen Storno ohne
 * Begruendung ab, und er steht spaeter auf dem Storno-Beleg.
 */
const StornoDialog: React.FC<{
    beleg: FreelancerCreditNote;
    grund: string;
    setGrund: (v: string) => void;
    laeuft: boolean;
    onClose: () => void;
    onBestaetigen: () => void;
}> = ({ beleg, grund, setGrund, laeuft, onClose, onBestaetigen }) => (
    <div className="fixed inset-0 z-[100] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-800">Gutschrift stornieren</h3>
                <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
                Es entsteht ein eigener Storno-Beleg mit negativen Beträgen zu{' '}
                <strong>{beleg.nummer}</strong>. Die enthaltenen Begehungen werden wieder
                abrechenbar. Der ursprüngliche Beleg bleibt unverändert bestehen.
            </p>
            <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                    Grund (Pflicht)
                </label>
                <textarea
                    autoFocus
                    value={grund}
                    onChange={(e) => setGrund(e.target.value)}
                    rows={3}
                    placeholder="z. B. falscher Zeitraum, Begehung doppelt erfasst"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                />
            </div>
            <div className="flex justify-end gap-3 pt-1">
                <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
                <Button
                    variant="danger"
                    onClick={onBestaetigen}
                    disabled={laeuft || !grund.trim()}
                    icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                >
                    Stornieren
                </Button>
            </div>
        </div>
    </div>
);
